import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Loader2, LogOut, RefreshCw, Phone, Mail, MapPin, Lock } from "lucide-react";

const TOKEN_KEY = "me_admin_token";

const ENDPOINTS = [
  { key: "leads", label: "Contact / Estimate Leads", path: "/leads" },
  { key: "callbacks", label: "Callback Bookings", path: "/callbacks" },
  { key: "financing", label: "Financing Inquiries", path: "/financing-inquiry" },
  { key: "adu", label: "ADU Calculator Leads", path: "/adu-leads" },
];

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
};

export default function AdminLeadsPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [draftToken, setDraftToken] = useState("");
  const [data, setData] = useState({ leads: [], callbacks: [], financing: [], adu: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");

  const fetchAll = async (t) => {
    setLoading(true);
    setError("");
    try {
      const results = await Promise.all(
        ENDPOINTS.map((e) =>
          axios
            .get(`${API}${e.path}`, { headers: { "X-Admin-Token": t } })
            .then((r) => [e.key, r.data])
            .catch((err) => {
              if (err?.response?.status === 401 || err?.response?.status === 403) throw err;
              return [e.key, []];
            })
        )
      );
      const merged = Object.fromEntries(results);
      setData(merged);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        setError("Invalid admin token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not load leads. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!draftToken.trim()) return;
    localStorage.setItem(TOKEN_KEY, draftToken.trim());
    setToken(draftToken.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData({ leads: [], callbacks: [], financing: [], adu: [] });
  };

  if (!token) {
    return (
      <section data-testid="admin-login" className="min-h-screen pt-32 pb-20 flex items-center bg-brand-bone">
        <div className="container-page max-w-md">
          <div className="card-base p-8">
            <Lock className="w-6 h-6 text-brand-olive" strokeWidth={1.75} />
            <h1 className="heading-2 mt-4 text-3xl">Admin · Leads</h1>
            <p className="body-sm mt-3">Enter your admin token to view all submitted leads.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-3">
              <input
                type="password"
                autoFocus
                data-testid="admin-token-input"
                placeholder="Paste admin token"
                value={draftToken}
                onChange={(e) => setDraftToken(e.target.value)}
                className="input-base"
              />
              <button data-testid="admin-login-btn" className="btn-primary w-full">
                Unlock
              </button>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </form>
            <p className="text-xs text-brand-ash mt-6 leading-relaxed">
              The token is set in the backend `ADMIN_TOKEN` env variable. Tokens are stored only in your
              browser's local storage on this device.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const counts = {
    leads: data.leads?.length || 0,
    callbacks: data.callbacks?.length || 0,
    financing: data.financing?.length || 0,
    adu: data.adu?.length || 0,
  };
  const total = counts.leads + counts.callbacks + counts.financing + counts.adu;

  return (
    <section data-testid="admin-leads-page" className="min-h-screen pt-28 pb-24 bg-brand-bone">
      <div className="container-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="overline">Admin</p>
            <h1 className="heading-2 mt-2">Submitted leads</h1>
            <p className="body-sm mt-2">{total} total submission{total === 1 ? "" : "s"}.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => fetchAll(token)}
              disabled={loading}
              className="btn-secondary"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button data-testid="admin-logout" onClick={handleLogout} className="btn-ghost">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 border-b border-brand-line">
          {ENDPOINTS.map((e) => (
            <button
              key={e.key}
              onClick={() => setTab(e.key)}
              data-testid={`admin-tab-${e.key}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
                tab === e.key
                  ? "border-brand-obsidian text-brand-ink"
                  : "border-transparent text-brand-ash hover:text-brand-ink"
              }`}
            >
              {e.label}
              <span className="ml-2 px-2 py-0.5 rounded-sm bg-brand-limestone text-brand-ink text-xs">
                {counts[e.key]}
              </span>
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

        {tab === "leads" && <LeadTable rows={data.leads || []} />}
        {tab === "callbacks" && <CallbackTable rows={data.callbacks || []} />}
        {tab === "financing" && <FinancingTable rows={data.financing || []} />}
        {tab === "adu" && <AduTable rows={data.adu || []} />}
      </div>
    </section>
  );
}

function EmptyRow({ msg }) {
  return (
    <div className="card-base p-10 text-center">
      <p className="body-sm">{msg}</p>
    </div>
  );
}

function LeadCard({ children }) {
  return <div className="card-base p-5">{children}</div>;
}

function LeadHeader({ name, when, service, source }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3 border-b border-brand-line pb-3">
      <div>
        <p className="font-heading text-lg font-bold text-brand-ink">{name || "—"}</p>
        <p className="text-xs text-brand-ash mt-0.5">{fmtDate(when)}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {service && <span className="badge-trust">{service}</span>}
        {source && <span className="text-xs text-brand-ash">{source}</span>}
      </div>
    </div>
  );
}

function ContactRow({ phone, email, address }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
      {phone && (
        <a href={`tel:${phone}`} className="flex items-center gap-2 text-brand-ink hover:text-brand-olive-hover">
          <Phone className="w-3.5 h-3.5 text-brand-olive" /> {phone}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className="flex items-center gap-2 text-brand-ink hover:text-brand-olive-hover truncate">
          <Mail className="w-3.5 h-3.5 text-brand-olive" /> {email}
        </a>
      )}
      {address && (
        <span className="flex items-center gap-2 text-brand-ash">
          <MapPin className="w-3.5 h-3.5 text-brand-olive" /> {address}
        </span>
      )}
    </div>
  );
}

function LeadTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No contact leads yet." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <LeadCard key={r.id}>
          <LeadHeader name={r.full_name} when={r.created_at} service={r.service_needed} source={r.source} />
          <ContactRow phone={r.phone} email={r.email} address={r.property_address} />
          {r.message && <p className="text-sm text-brand-ash mt-3 whitespace-pre-wrap">{r.message}</p>}
        </LeadCard>
      ))}
    </div>
  );
}

function CallbackTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No callbacks booked yet." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <LeadCard key={r.id}>
          <LeadHeader name={r.full_name} when={r.created_at} service={r.service_needed} source={r.status} />
          <p className="text-sm font-semibold text-brand-ink mb-3">
            Scheduled: {r.scheduled_date} at {r.scheduled_time}
          </p>
          <ContactRow phone={r.phone} email={r.email} />
          {r.notes && <p className="text-sm text-brand-ash mt-3 whitespace-pre-wrap">{r.notes}</p>}
        </LeadCard>
      ))}
    </div>
  );
}

function FinancingTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No financing inquiries yet." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <LeadCard key={r.id}>
          <LeadHeader name={r.full_name} when={r.created_at} service={r.project_type} />
          <ContactRow phone={r.phone} email={r.email} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
            <div><span className="text-brand-ash">Budget: </span>{r.estimated_budget || "—"}</div>
            <div><span className="text-brand-ash">Project: </span>{r.project_type || "—"}</div>
          </div>
          {r.message && <p className="text-sm text-brand-ash mt-3 whitespace-pre-wrap">{r.message}</p>}
        </LeadCard>
      ))}
    </div>
  );
}

function AduTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No ADU calculator leads yet." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <LeadCard key={r.id}>
          <LeadHeader name={r.full_name} when={r.created_at} service={r.adu_type} source={`${r.size_sqft} sq ft`} />
          <ContactRow phone={r.phone} email={r.email} address={r.property_address} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 p-3 bg-brand-limestone rounded-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-ash">Reasonable</p>
              <p className="font-heading text-lg font-bold">{fmtUSD(r.reasonable_estimate)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-ash">Expected</p>
              <p className="font-heading text-lg font-bold">
                {fmtUSD(r.expected_low)}–{fmtUSD(r.expected_high)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-ash">High-end</p>
              <p className="font-heading text-lg font-bold">{fmtUSD(r.high_end_estimate)}+</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs text-brand-ash">
            <div><span className="font-semibold text-brand-ink">Finish:</span> {r.finish_level}</div>
            <div><span className="font-semibold text-brand-ink">Bath:</span> {r.bathrooms}</div>
            <div><span className="font-semibold text-brand-ink">Kitchen:</span> {r.kitchen_type}</div>
            <div><span className="font-semibold text-brand-ink">Foundation:</span> {r.foundation}</div>
            <div className="col-span-2 md:col-span-3"><span className="font-semibold text-brand-ink">Utilities:</span> {(r.utilities || []).join(", ") || "—"}</div>
            <div><span className="font-semibold text-brand-ink">Site:</span> {r.site_condition}</div>
          </div>
          {r.preferred_time && <p className="text-sm text-brand-ink mt-3"><span className="text-brand-ash">Preferred time: </span>{r.preferred_time}</p>}
          {r.project_notes && <p className="text-sm text-brand-ash mt-2 whitespace-pre-wrap">{r.project_notes}</p>}
        </LeadCard>
      ))}
    </div>
  );
}
